import React from "react";
import { apiClient } from "@/api/apiClient";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Heart, X, Eye, Calendar } from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import CategoryManager from "../components/categories/CategoryManager";
import SkeletonListItem from "../components/ui/SkeletonListItem";
import { getOptimizedImageUrl } from "@/utils";

export default function HistoriquePage() {
    const { data: interactions = [], isLoading } = useQuery({
        queryKey: ['interactions'],
        queryFn: async () => {
            try { await apiClient.auth.me(); } catch (e) { /* Guest mode */ }
            const res = await apiClient.entities.UserInteraction.list();
            // console.log("[Historique] Loaded interactions:", res);
            return res;
        },
        refetchOnMount: 'always',
    });

    const getActionIcon = (action) => {
        switch (action) {
            case 'swipe_right':
            case 'like':
                return <Heart className="w-5 h-5 text-green-500 fill-green-500" />;
            case 'swipe_left':
            case 'dislike':
                return <X className="w-5 h-5 text-red-500" />;
            default:
                return <Eye className="w-5 h-5 text-amber-500" />;
        }
    };

    const getActionLabel = (action) => {
        switch (action) {
            case 'swipe_right':
            case 'like':
                return 'Liké';
            case 'swipe_left':
            case 'dislike':
                return 'Passé';
            default:
                return 'Vu';
        }
    };

    const getActionColor = (action) => {
        switch (action) {
            case 'swipe_right':
            case 'like':
                return 'bg-green-100 text-green-800 border-green-200';
            case 'swipe_left':
            case 'dislike':
                return 'bg-red-100 text-red-800 border-red-200';
            default:
                return 'bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-900/30';
        }
    };

    const filteredInteractions = interactions.filter(i =>
        ['like', 'dislike', 'swipe_right', 'swipe_left'].includes(i.action)
    );

    // Safety check just in case API returns weird stuff
    const displayableInteractions = filteredInteractions.filter(i => i.product && i.product.name);

    if (isLoading) {
        return (
            <div className="min-h-screen p-3 md:p-8" aria-busy="true" aria-label="Chargement de l'historique">
                <div className="max-w-4xl mx-auto">
                    {/* Header skeleton */}
                    <div className="mb-6 md:mb-8">
                        <div className="skeleton-bone skeleton-stagger-1" style={{ height: '1.875rem', width: '8rem' }} />
                        <div className="skeleton-bone skeleton-stagger-2 mt-2" style={{ height: '0.875rem', width: '10rem' }} />
                    </div>
                    {/* List items skeleton */}
                    <div className="space-y-4">
                        {Array.from({ length: 6 }, (_, i) => (
                            <SkeletonListItem key={i} index={i} />
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen p-3 md:p-8">
            <div className="max-w-4xl mx-auto">
                <div className="mb-6 md:mb-8">
                    <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-2">Historique</h1>
                    <p className="text-gray-600">
                        {displayableInteractions.length} interaction{displayableInteractions.length !== 1 ? 's' : ''} au total
                    </p>
                </div>

                {displayableInteractions.length === 0 ? (
                    <div className="flex items-center justify-center py-20">
                        <div className="text-center max-w-md">
                            <div className="w-24 h-24 bg-gradient-to-br from-amber-100 to-yellow-100 dark:from-amber-950/30 dark:to-yellow-950/30 rounded-full flex items-center justify-center mx-auto mb-6">
                                <Calendar className="w-12 h-12 text-amber-600 dark:text-amber-400" />
                            </div>
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
                                Aucun historique
                            </h2>
                            <p className="text-gray-600">
                                Votre historique de swipes apparaîtra ici
                            </p>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {displayableInteractions.map((interaction, idx) => {
                            // Use the product object embedded in the interaction
                            const product = interaction.product;
                            if (!product || !product.name) return null; // Safety check

                            const commonDate = new Date(interaction.created_at || interaction.created_date);
                            const isValidDate = !isNaN(commonDate.getTime());
                            const formattedDate = isValidDate
                                ? format(commonDate, "d MMMM yyyy 'à' HH:mm", { locale: fr })
                                : "Date inconnue";

                            const isOutOfStock = product.stock_status === 'rupture' ||
                                !product.sizes ||
                                product.sizes.length === 0 ||
                                (product.sizes.length === 1 && !product.sizes[0]) ||
                                product.sizes.every(size => typeof size === 'string' && size.includes('(Out of Stock)'));

                            const handleCardClick = (e) => {
                                // Prevent navigation if clicking on interactive elements
                                if (
                                    e.target.closest('button') ||
                                    e.target.closest('[role="button"]') ||
                                    e.target.closest('.no-click-nav')
                                ) {
                                    return;
                                }
                                window.location.hash = `#/product?id=${product.id}`;
                            };

                            return (
                                <Card
                                    key={interaction.id}
                                    className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
                                    onClick={handleCardClick}
                                >
                                    <CardContent className="p-3 md:p-4">
                                        <div className="flex gap-3 md:gap-4">
                                            <div className="w-20 h-20 md:w-24 md:h-24 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                                                <img
                                                    src={product.images && product.images.length > 0 ? getOptimizedImageUrl(product.images[0], 160) : 'https://placehold.co/100x100?text=No+Image'}
                                                    alt={product.name}
                                                    className="w-full h-full object-cover"
                                                    loading="lazy"
                                                />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex-1 min-w-0 flex flex-col justify-between h-full">
                                                    <div>
                                                        <div className="flex items-start justify-between gap-1 mb-1">
                                                            <div className="flex-1 min-w-0 pr-1">
                                                                <h3 className="font-bold text-sm md:text-base text-gray-900 dark:text-white line-clamp-2 leading-tight">
                                                                    {product.name}
                                                                </h3>
                                                                <p className="text-xs text-amber-600 dark:text-amber-400 font-medium truncate mt-0.5">
                                                                    {product.brand}
                                                                </p>
                                                            </div>
                                                            <div className="text-right flex-shrink-0">
                                                                <span className="font-bold text-gray-900 dark:text-white text-base md:text-lg block">{product.price}€</span>
                                                            </div>
                                                        </div>

                                                        {isOutOfStock && (
                                                            <div className="mb-1">
                                                                <Badge className="bg-gray-800 text-white text-[10px] h-5 px-1.5 border-white/20 whitespace-nowrap hover:bg-gray-800 inline-flex">
                                                                    Rupture
                                                                </Badge>
                                                            </div>
                                                        )}
                                                    </div>

                                                    <div className="flex items-center justify-between mt-auto pt-1">
                                                        <Badge variant="outline" className={`${getActionColor(interaction.action)} border text-xs px-2 py-0.5 h-6`}>
                                                            <span className="mr-1">{getActionIcon(interaction.action)}</span>
                                                            {getActionLabel(interaction.action)}
                                                        </Badge>
                                                    </div>

                                                    {/* Catégories personnalisées */}
                                                    <div className="mt-3 mb-1 no-click-nav" onClick={(e) => e.stopPropagation()}>
                                                        <CategoryManager
                                                            productId={product.id}
                                                            size="sm"
                                                        />
                                                    </div>

                                                    <div className="flex items-center text-[10px] md:text-sm text-gray-500 mt-1">
                                                        <Calendar className="w-3 h-3 md:w-4 md:h-4 mr-1" />
                                                        {formattedDate}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </div >
                )}
            </div >
        </div >
    );
}
